import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"
import styled from "styled-components"

import { IDLE, PENDING, STORAGE_GOOGLE } from "const"
import {
    MtDriveCloud,
    GoogleCredentials,
} from "server/models"
import { StateType, useInput } from "client/utils"
import {
    StoreType,
    GoogleCreateServerClient,
    GoogleGenerateAuthUrl,
    GoogleConfirmCode,
    CreateCloudDriveSettings,
    SaveAccountSettings,

    updateAccountSettings,
    setSystemAccountSettings,
    closeModal,
} from "client/store"
import {
    Container,
    PageContent,
    PageHeader,
    PageTitle,
    Collapse,
    PageForm,
    FormGroup,
    Label,
    FileLoader,
    Input,
    Feedback,
    Button,
    SpinnerIcon,
    BalloonMessage,
} from "client/components"

const ConfirmLink = styled.a`
    text-decoration: underline;
`

let isMounted: boolean

const GoogleDriveSecrets = () => {
    const accountSettings = useSelector((state: StoreType) => state.accountSettings.settings)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [credentialsJSON, setCredentialsJSON] = useState<string>(null)
    const [authUrl, setAuthUrl] = useState<string>(null)
    const [confirmCode, setConfirmCode] = useInput("")

    const [credentialsValidation, setCredentialsValidation] = useState<string>(null)
    const [confirmCodeValidation, setConfirmCodeValidation] = useState<string>(null)

    const [credentialsLoadState, setCredentialsLoadState] = useState<StateType>(IDLE)
    const [confirmCodeState, setConfirmCodeState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return
        if (modals.length) dispatch(closeModal())
    }

    const onSetCredentials = () => {
        setCredentialsValidation(null)
        const credentials = JSON.parse(credentialsJSON) as GoogleCredentials
        if (
            !credentials.web
            || !credentials.web.client_id
            || !credentials.web.client_secret
            || !credentials.web.redirect_uris
            || !credentials.web.redirect_uris[0]
        ) {
            setCredentialsValidation(lang.wrongConfigFormatting)
            return
        }

        setCredentialsLoadState(PENDING)

        GoogleCreateServerClient({
            accountId: userData.accountId,
            credentialsJSON,
        }).then(() => {
            GoogleGenerateAuthUrl(
                { accountId: userData.accountId },
                lang,
            ).then((res) => {
                setCredentialsLoadState(IDLE)
                if (res.validation) setCredentialsValidation(res.validation)
                else {
                    setAuthUrl(res.authUrl)
                    window.open(res.authUrl, "_blank").focus()
                }
            })
        })
    }

    const onConfirmCode = () => {
        setConfirmCodeState(PENDING)
        GoogleConfirmCode(
            {
                accountId: userData.accountId,
                confirmCode,
            },
            lang,
        ).then((res) => {
            if (res.validation) {
                setConfirmCodeState(IDLE)
                setConfirmCodeValidation(res.validation)
                return
            }

            const cloudDrive: MtDriveCloud = {
                accountId: userData.accountId,
                storage: STORAGE_GOOGLE,
                credentials: credentialsJSON,
                token: JSON.stringify(res.token),
            }

            CreateCloudDriveSettings(cloudDrive).then(() => {
                dispatch(updateAccountSettings( // update in store
                    "drive",
                    {
                        defaultStorage: STORAGE_GOOGLE,
                        googleDriveConnected: true,
                    },
                ))

                const accountSettingsCopy = { ...accountSettings }
                accountSettingsCopy.drive = { // update for saving
                    ...accountSettingsCopy.drive,
                    defaultStorage: STORAGE_GOOGLE,
                    googleDriveConnected: true,
                }

                SaveAccountSettings(userData.accountId, accountSettingsCopy)
                .then(() => {
                    dispatch(setSystemAccountSettings(accountSettingsCopy))
                    setConfirmCodeState(IDLE)
                    onClose()
                })
            })
        })
    }

    const confirmLink = lang.cloudDriveConfirmLink.split("{0}")

    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.googleDriveSecrets}</PageTitle>
            </PageHeader>
            <PageContent>
                <Collapse
                    show
                    title={lang.connectDriveStep1SetCredentials}
                >
                    <PageForm>
                        <Row>
                            <Col xs={12}>
                                <FormGroup>
                                    <Label>{lang.credentials}</Label>
                                    <FileLoader
                                        type="file"
                                        forceAccept="application/json"
                                        value={null}
                                        autoLoad={false}
                                        onLoad={(file) => { // set credentials after the file is selected
                                            const reader = new FileReader()
                                            reader.readAsText(file)

                                            reader.onload = () => {
                                                setCredentialsJSON(reader.result as string)
                                            }

                                            reader.onerror = () => {
                                                BalloonMessage("Error while opening the file. Check console")
                                                // eslint-disable-next-line no-console
                                                console.log(reader.error)
                                            }
                                        }}
                                        callback={(file) => { // clear credentials on delete
                                            if (!file) setCredentialsJSON(null)
                                        }}
                                    />
                                    <Feedback>{credentialsValidation && credentialsValidation}</Feedback>
                                </FormGroup>
                                <Button
                                    variant="success"
                                    disabled={!credentialsJSON}
                                    onClick={onSetCredentials}
                                >
                                    {credentialsLoadState === PENDING ? <SpinnerIcon /> : lang.connect}
                                </Button>
                            </Col>
                        </Row>
                    </PageForm>
                </Collapse>
                {
                    authUrl !== null && (
                        <Collapse
                            show
                            title={lang.connectDriveStep2EnterConfirmCode}
                        >
                            <PageForm>
                                <Row>
                                    <Col xs={12}>
                                        <p>
                                            {confirmLink[0]}
                                            <ConfirmLink href={authUrl} target="_blank" rel="noopener noreferrer">
                                                {lang.thisLink}
                                            </ConfirmLink>
                                            {confirmLink[1]}
                                        </p>

                                        <FormGroup>
                                            <Label>{lang.confirmCode}</Label>
                                            <Input type="text" value={confirmCode} onChange={setConfirmCode} />
                                            <Feedback>{confirmCodeValidation && confirmCodeValidation}</Feedback>
                                        </FormGroup>
                                        <Button
                                            variant="success"
                                            disabled={!confirmCode.length}
                                            onClick={onConfirmCode}
                                        >
                                            {confirmCodeState === PENDING ? <SpinnerIcon /> : lang.confirm}
                                        </Button>
                                    </Col>
                                </Row>
                            </PageForm>
                        </Collapse>
                    )
                }
            </PageContent>
        </Container>
    )
}
export default GoogleDriveSecrets
